const PATH_SEPARATOR = '.';


/**
 * @param value {any} Value to check for array
 * @return {boolean} True if value is array, false if not
 */
export const isArray = value => {

    return Array.isArray(value);
};


/**
 * @param value {any} Value to check for plain object
 * @return {boolean} True if plain object; false if not
 */
export const isObject = value => {

    return value
        && typeof value === 'object'
        && !isArray(value)
        && value.constructor
        && value.constructor.name === 'Object';
};


export const copy = object => {

    if (Array.isArray(object)) {

        return [...object];
    }

    return {...object};
};


/**
 * @param object {array|object} Object to iterate over
 * @param callback {function} Callback called for each entry; (key, value)
 */
export const each = (object, callback) => {

    if (Array.isArray(object)) {

        object.forEach(([key, value]) => callback(key, value));

        return;
    }

    Object.entries(object)
        .forEach(([key, value]) => callback(key, value));
};


/**
 * @param object {array|object} Object to iterate over
 * @param callback {function} Callback called for each entry; (key, value)
 * @param recursiveKey {string} Name of the key to check for recursive iteration
 */
export const eachRecursively = (object, callback, recursiveKey) => {

    const callbackApplier = (key, value) => {

        callback(key, value);
        const recursiveItems = value[recursiveKey];
        if (recursiveItems) {

            if (!isObject(recursiveItems) && !isArray(recursiveItems)) {

                throw Error(
                    'cannot apply eachRecursively because value of '
                    + ` recursiveKey '${recursiveKey}' is neither array nor object`
                );
            }

            eachRecursively(recursiveItems, callback, recursiveKey);
        }
    };

    if (Array.isArray(object)) {

        object.forEach((value, index) => callbackApplier(index, value));

        return;
    }

    Object.entries(object)
        .forEach(([key, value]) => callbackApplier(key, value));
};


/**
 * Filters the given object by the predicate and returns a new object with the filtered values.
 * The predicate receives key and value as arguments.
 *
 * @param object {object} Object to check
 * @param predicate {function}
 * @return {object} Filtered object
 */
export const filter = (object, predicate) => {

    const filteredObject = {};
    each(object, (key, value) => {
        if (predicate(key, value)) {
            filteredObject[key] = value;
        }
    });

    return filteredObject;
};


/**
 * @param object {array|object} Check if empty or not
 * @return {boolean} True if empty, false if not
 */
export const isEmpty = object => {

    if (isArray(object)) {

        return object.length === 0;
    }

    return Object.keys(object).length === 0;
};


/**
 * @param object {array|object} Object to iterate over
 * @param mappingFunction {function} Callback called for each entry (key, value)
 * @return {{}|*}
 */
export const map = (object, mappingFunction) => {

    if (Array.isArray(object)) {

        return object.map((value, key) => mappingFunction(key, value));
    }

    const mapped = {};
    each(object, (key, value) => {
        mapped[key] = mappingFunction(key, value);
    });

    return mapped;
};


/**
* @param object {array|object} Object to iterate over
* @param mappingFunction {function} Callback called for each entry (key, value)
* @return {{}|*}
*/
export const mapRecursive = (object, mappingFunction) => {

    return map(object, (key, value) => {

        if (isObject(value)) {

            return map(value, mappingFunction);
        }

        return mappingFunction(key, value);
    });
};

/**
 * @param object {object} Object to return mapped values for
 * @param mappingFunction {function} Callback called for each entry (key, value)
 * @return {{}|*}
 */
export const mapToArray = (object, mappingFunction) => {

    const mapped = map(object, mappingFunction);

    return Object.values(mapped);
};


/**
 * @param object {object} Object to get data form
 * @param path {string} Field name or path separated by "." to retrieve nested values
 * @param defaultValue {any|null} Return value in case the field doesn't exist
 * @param resolver {function|null} Resolver for an existing entry to e.g. adhere nested structuring
 * @return {any} Value of existing (nested) field; otherwise default value
 */
export const findInObject = (object, path, defaultValue = null, resolver = null) => {

    const separatorPosition = path.indexOf(PATH_SEPARATOR);
    if (separatorPosition !== -1) {

        const rootPath = path.substring(0, separatorPosition);
        if (!object.hasOwnProperty(rootPath)) {

            return defaultValue;
        }

        const subPath = path.substring(separatorPosition + 1);
        let subContext = object[rootPath];
        if (resolver) {
            subContext = resolver(subContext);
        }

        return findInObject(subContext, subPath, defaultValue, resolver);
    }

    if (!object.hasOwnProperty(path)) {

        return defaultValue;
    }

    return object[path];
};


export const merge = (object1, object2) => {

    const mergedObject = {...object1};

    each(object2, (key, value) => {

        if (isObject(value)) {
            const existingValue = object1[key];
            value = merge(existingValue ? existingValue : {}, value);
        }
        mergedObject[key] = value;
    });

    return mergedObject;
};


export const flattenArray = (array, result = []) => {

    for (let i = 0, length = array.length; i < length; i++) {

        const value = array[i];
        if (Array.isArray(value)) {
            flattenArray(value, result);
        } else {
            result.push(value);
        }
    }

    return result;
};


/**
 * @param obj {object} Object to flatten recursively
 * @param prefix {null|string} Prefix to add
 * @param separator {string} Path separator, defaults to "."
 * @return {{}} Flattened object
 */
export const flattenObject = (obj, prefix = null, separator = '.') => {

    let flattenedObject = {};
    each(obj, (key, value) => {

        const flattenedKey = prefix ? `${prefix}${separator}${key}` : key;
        if (isObject(value)) {
            flattenedObject = {
                ...flattenedObject,
                ...flattenObject(value, flattenedKey)
            };
        } else {
            flattenedObject[flattenedKey] = value;
        }
    });

    return flattenedObject;
};


export const isSomething = variable => {

    return typeof variable !== 'undefined' && variable !== null;
};


/**
 * @param object1 {object} Object to check for differences
 * @param object2 {object} Object to check for differences against
 * @return {boolean} True of objects differ; false if not
 */
export const isDifferent = (object1, object2) => {

    if (typeof object1 !== typeof object2) {

        return true;
    }

    return JSON.stringify(object1) !== JSON.stringify(object2);
};


/**
 * @param object1 {object} Object to check for differences
 * @param object2 {object} Object to check for differences against
 * @return {boolean} True of objects are equal; false if not
 */
export const isEqual = (object1, object2) => {

    return !isDifferent(object1, object2);
};


/**
 * @param objects {array|object[]} List of objects to key by the value of the given fieldName
 * @param fieldName {string} Field to key by
 */
export const keyBy = (objects, fieldName) => {

    const keyedBy = {};

    objects.forEach(object => {

        keyedBy[object[fieldName]] = object;
    });

    return keyedBy;
};


/**
 * @param object {object} Object to check
 * @return {boolean} True if all values are non-falsy/populated
 */
export const areAllValuesPresent = object => {

    return !Object.values(object)
        .find(value => !value);
};


/**
 * @param object {array|object} Array/object to find index/key for the given value
 * @param value {any} Value to check for
 * @return {number|any} Found index/key or null if not exists
 */
export const findKey = (object, value) => {

    if (Array.isArray(object)) {

        const index = object.findIndex(currentValue => currentValue === value);

        return index !== -1
            ? index
            : null;
    }

    const found = Object.entries(object)
        .map(([key, currentValue]) => {

            return {key, currentValue};
        })
        .find(({key, currentValue}) => currentValue === value);

    return found && found.key || null;
};


/**
 * @param object {array|object} Array/object to remove keys from
 * @param keysToRemove {array|string[]} Keys to remove from
 * @return {array|object} Cleaned array/object
 */
export const removeKeys = (object, keysToRemove) => {

    if (Array.isArray(object)) {

        const cleanedArray = [];
        object.forEach((value, index) => {
            if (!keysToRemove.includes(index)) {
                cleanedArray.push(value);
            }
        });

        return cleanedArray;
    }

    const cleanedObject = {};
    Object.keys(object)
        .filter(key => !keysToRemove.includes(key))
        .forEach(key => {
            cleanedObject[key] = object[key];
        });

    return cleanedObject;
};


/**
 * @param object {array|object} Array/object to remove key from
 * @param keyToRemove {any} Key to remove from
 * @return {array|object} Cleaned array/object
 */
export const removeKey = (object, keyToRemove) => {

    return removeKeys(object, [keyToRemove]);
};


/**
 * @param object {object|array} Object or array to remove value from
 * @param valueToRemove {any} Value to remove
 * @return {object|array} Object or array without the given value, retains keys
 */
export const removeValue = (object, valueToRemove) => {

    const key = findKey(object, valueToRemove);
    if (key === null) {

        return copy(object);
    }

    return removeKeys(object, [key]);
};


/**
 * @param object {object} Object to remove all keys from but the given ones
 * @param keysToRetain {array|string[]} Keys to retain
 * @return {object} Cleaned object
 */
export const retainKeys = (object, keysToRetain) => {

    const keysToRemove = Object.keys(object)
        .filter(key => !keysToRetain.includes(key));

    return removeKeys(object, keysToRemove);
};


/**
 * @param list {array} Array to remove all values from but the given ones (filter-wrapper)
 * @param valuesToRetain {array|any[]} Values to retain
 * @return {array} Cleaned list
 */
export const retainValues = (list, valuesToRetain) => {

    return list.filter(value => valuesToRetain.includes(value));
};


/**
 * @param list {array} Array to remove all values from but the given one (filter-wrapper)
 * @param valueToRetain {any} Value to retain
 * @return {array} Cleaned list
 */
export const retainValue = (list, valueToRetain) => {

    return retainValues(list, [valueToRetain]);
};


/**
 * @param object1 {object} Object to compare against
 * @param object2 {object} Object to compare with
 * @return {array|string[]} List of keys that are present in object1 but not in object2
 */
export const getMissingKeys = (object1, object2) => {

    const keys2 = Object.keys(object2);

    return Object.keys(object1)
        .filter(key1 => !keys2.includes(key1));
};


/**
 * @param object1 {object} Object 1 to compare
 * @param object2 {object} Object 2 to compare
 * @param property {string} Property to compare by
 * @param direction {number} Sort order: 1 for asc, -1 desc
 */
export const compareSort = (object1, object2, property, direction = 1) => {

    const p1 = object1[property] || null;
    const p2 = object2[property] || null;
    const isAscending = direction === 1;

    if (p1 < p2) {

        return isAscending
            ? -1
            : 1;
    }
    if (p1 > p2) {

        return isAscending
            ? 1
            : -1;
    }

    return 0;
};


/**
 * @param list {array|object[]} Array of objects to sort
 * @param sortProperties {object} Object of propertyName to sort order
 */
export const sortBy = (list, sortProperties) => {

    return list.sort((o1, o2) => {

        const entries = Object.entries(sortProperties);
        for (let i = 0; i < entries.length; i++) {
            const [property, dir] = entries[i];
            const sortResult = compareSort(o1, o2, property, dir);
            if (sortResult !== 0) {

                return sortResult;
            }
        }

        return 0;
    });
};


/**
 * Adds the given value if it is not in list or removes it, if already existing
 *
 * @param list {array} List to either add or remove value from
 * @param value {any} Value to add or remove
 * @return {array} List after modifications
 */
export const toggleValue = (list, value) => {

    if (list.includes(value)) {

        return list.filter(entry => entry !== value);
    }

    return [...list, value];
};


/**
 * Returns the first value if exists or the default value
 *
 * @param list {array} Array to return first value from, if existing
 * @param defaultValue {any} Default return in case array has no elements
 */
export const firstValue = (list, defaultValue = null) => {

    if (list.length > 0) {

        return list[0];
    }

    return defaultValue;
};


/**
 * Returns only unique values using the JSON string representation
 *
 * @param list {array|any[]} List with entries
 * @return {array|any[]} Unique values
 */
export const unique = list => {

    const uniqueStringValues = [];
    const uniqueValues = [];
    list.forEach(value => {

        const stringValue = JSON.stringify(value);
        if (!uniqueStringValues.includes(stringValue)) {
            uniqueValues.push(value);
            uniqueStringValues.push(stringValue);
        }
    });

    return uniqueValues;
};
